// =============================================
// PublishedQAPanel — 4. faza pipeline'u (Sprawdzanie po publikacji).
// =============================================
// Layout: compact table + inline expand z pełnym detail po kliku w wiersz.
// LLM summary truncated w tabeli + pełen w expanded sub-row.
// Filter: all / warnings / failed / has_issues / halucynations / not_audited.
// Klik [Podgląd treści] → modal z LIVE HTML rendered iframe.
// =============================================

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  Play,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  MinusCircle,
  Eye,
  Sparkles,
  ChevronRight,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import { useThematicNodeContent } from "@/hooks/useThematicNodeContent";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useContentAudits } from "@/hooks/useContentAudits";
import { LiveIndicator } from "@/components/ui/LiveIndicator";
import { ContentPreviewDialog } from "./ContentPreviewDialog";
import type { ContentAuditResult } from "@/services/contentAuditService";
import { getBrokenOutgoingLinks, computeBrokenOutgoingStats } from "@/services/contentEngine/brokenOutgoingLinks";
import { scanDeadLinksForMap, type DeadLinkScan } from "@/services/audit/deadLinkDetector";
import { LinkFixPreviewModal } from "./LinkFixPreviewModal";
import { Link2 } from "lucide-react";

interface Props {
  mapId: string;
}

type FilterMode = "all" | "warnings" | "failed" | "has_issues" | "halucinations" | "has_dead_links" | "not_audited";

const FILTER_LABELS: Record<FilterMode, string> = {
  all: "Wszystkie",
  warnings: "Warnings",
  failed: "Failed",
  has_issues: "Z problemami",
  halucinations: "Z halucynacjami",
  has_dead_links: "Z martwymi linkami",
  not_audited: "Nie-audytowane",
};

function StatusIcon({ status }: { status: "passed" | "warning" | "failed" | "ok" | null | undefined }) {
  if (status === "ok" || status === "passed") {
    return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  }
  if (status === "warning") return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-red-500" />;
  return <MinusCircle className="h-4 w-4 text-muted-foreground/40" />;
}

function computeIssueCount(audit: ContentAuditResult): number {
  let total = 0;
  // Halucynacje LLM = MEDIUM/HIGH są problemami, LOW ignorujemy
  const haluc = audit.layer_llm?.semantic_check?.halucinations || [];
  total += haluc.filter((h) => h.severity !== "low").length;
  // Voice issues
  total += audit.layer_llm?.semantic_check?.voice_issues?.length || 0;
  // Content check
  if (audit.layer_llm?.content_check?.completeness === "truncated") total++;
  if (audit.layer_llm?.content_check?.hooks_natural === false) total++;
  if (audit.layer_llm?.content_check?.closure_natural === false) total++;
  if (audit.layer_llm?.semantic_check?.stays_on_topic === false) total++;
  // Completeness layer issues
  total += audit.layer_completeness?.issues?.length || 0;
  // Schema banned tokens (architecture violation)
  total += audit.layer_schema?.banned_tokens_found?.length || 0;
  if (audit.layer_schema && !audit.layer_schema.has_blogposting) total++;
  // SEO meta issues (DB level)
  if (audit.layer_seo_meta) {
    const titleLen = audit.layer_seo_meta.expected_title?.length || 0;
    const descLen = audit.layer_seo_meta.expected_description?.length || 0;
    if (!audit.layer_seo_meta.expected_title) total++;
    else if (titleLen < 30 || titleLen > 70) total++;
    if (!audit.layer_seo_meta.expected_description) total++;
    else if (descLen < 120 || descLen > 170) total++;
  }
  return total;
}

function hasHalucinations(audit: ContentAuditResult): boolean {
  const haluc = audit.layer_llm?.semantic_check?.halucinations || [];
  return haluc.some((h) => h.severity !== "low");
}

export function PublishedQAPanel({ mapId }: Props) {
  const { contents } = useThematicNodeContent(mapId);
  const { nodes } = useThematicNodes(mapId);
  const ca = useContentAudits(mapId);

  const [includeLLM, setIncludeLLM] = useState(true);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [previewDialog, setPreviewDialog] = useState<{ nodeId: string; title: string; wpPostUrl: string | null } | null>(null);
  // STOP-3 link-audit: dead-link scan map (per published node) + modal trigger
  const [deadLinkScansMap, setDeadLinkScansMap] = useState<Map<string, DeadLinkScan>>(new Map());
  const [linkFixModal, setLinkFixModal] = useState<{ nodeId: string } | null>(null);
  const [scanRefreshTick, setScanRefreshTick] = useState(0);

  const nodeTitleMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nodes) m.set(n.id, n.title || "");
    return m;
  }, [nodes]);

  const publishedArticles = useMemo(
    () => contents.filter((c) => (c as { engine_stage?: string }).engine_stage === "published"),
    [contents],
  );

  // Broken OUTGOING links — per published node ile linków do nieopublikowanych nodów (404).
  // Info-only widok (brak akcji — unpublish jest w zakładce Publikacje, regenerację robisz w Treści przez Priorytet linków).
  const [brokenOutgoingMap, setBrokenOutgoingMap] = useState<Map<string, { count: number; missingSlugs: string[] }>>(new Map());
  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;
    getBrokenOutgoingLinks(mapId).then((scores) => {
      if (!cancelled) setBrokenOutgoingMap(scores);
    }).catch(() => {
      // Silent — info card po prostu nie pokaże się
    });
    return () => { cancelled = true; };
  }, [mapId]);
  const brokenOutgoingStats = useMemo(() => computeBrokenOutgoingStats(brokenOutgoingMap), [brokenOutgoingMap]);

  // STOP-3 link-audit: scan martwych linków per artykuł (3 typy z liveness check po wp_post_url).
  // Refetch przy zmianie mapId lub po apply (scanRefreshTick increment).
  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;
    scanDeadLinksForMap(mapId).then((scans) => {
      if (cancelled) return;
      const m = new Map<string, DeadLinkScan>();
      for (const s of scans) m.set(s.sourceNodeId, s);
      setDeadLinkScansMap(m);
    }).catch(() => {
      // Silent — badge/filter po prostu pokaże 0
    });
    return () => { cancelled = true; };
  }, [mapId, scanRefreshTick]);

  const deadLinksAffectedCount = deadLinkScansMap.size;

  // Computed: issue count per audit
  const issueCountMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const [nodeId, audit] of ca.audits.entries()) {
      m.set(nodeId, computeIssueCount(audit));
    }
    return m;
  }, [ca.audits]);

  const filteredArticles = useMemo(() => {
    if (filter === "all") return publishedArticles;
    // has_dead_links — niezależny od audit (działa nawet gdy artykuł not_audited)
    if (filter === "has_dead_links") {
      return publishedArticles.filter((c) => deadLinkScansMap.has(c.node_id));
    }
    return publishedArticles.filter((c) => {
      const audit = ca.audits.get(c.node_id);
      if (filter === "not_audited") return !audit;
      if (!audit) return false;
      if (filter === "warnings") return audit.audit_status === "warning";
      if (filter === "failed") return audit.audit_status === "failed";
      if (filter === "has_issues") return (issueCountMap.get(c.node_id) || 0) > 0;
      if (filter === "halucinations") return hasHalucinations(audit);
      return true;
    });
  }, [publishedArticles, filter, ca.audits, issueCountMap, deadLinkScansMap]);

  const notAuditedCount = publishedArticles.filter((c) => !ca.audits.get(c.node_id)).length;
  const issuesCount = Array.from(issueCountMap.values()).filter((v) => v > 0).length;
  const halucinationsCount = Array.from(ca.audits.values()).filter(hasHalucinations).length;

  const totalCostUsd = useMemo(() => {
    let sum = 0;
    for (const a of ca.audits.values()) sum += a.cost_usd || 0;
    return sum;
  }, [ca.audits]);

  const toggleSelect = useCallback((nodeId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === filteredArticles.length) return new Set();
      return new Set(filteredArticles.map((c) => c.node_id));
    });
  }, [filteredArticles]);

  const toggleExpand = useCallback((nodeId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const handleBulkAudit = useCallback(() => {
    const targets =
      selected.size > 0
        ? Array.from(selected)
        : publishedArticles.filter((c) => !ca.audits.get(c.node_id)).map((c) => c.node_id);
    if (targets.length === 0) return;
    const hasAudited = targets.some((nodeId) => ca.audits.has(nodeId));
    const forceRefresh = selected.size > 0 && hasAudited;
    const llmCost = includeLLM ? (targets.length * 0.003).toFixed(2) : "0.00";
    if (
      !confirm(
        `${forceRefresh ? "PONOWNY audyt" : "Audyt"} ${targets.length} artykułów?\n\n` +
          `Layers 1-5: FREE\n` +
          `Layer 6 LLM: ${includeLLM ? `~$${llmCost} (${targets.length} × $0.003)` : "OFF"}\n\n` +
          `Czas: ~${Math.ceil(targets.length / 3)} s (concurrency 3)`,
      )
    )
      return;
    ca.runBulk(targets, { includeLLM, forceRefresh });
    setSelected(new Set());
  }, [selected, publishedArticles, ca, includeLLM]);

  const handleSingleAudit = useCallback(
    (nodeId: string) => {
      ca.runSingle(nodeId, { includeLLM, forceRefresh: false });
    },
    [ca, includeLLM],
  );

  const filterCounts: Record<FilterMode, number> = {
    all: publishedArticles.length,
    warnings: ca.warningCount,
    failed: ca.failedCount,
    has_issues: issuesCount,
    halucinations: halucinationsCount,
    has_dead_links: deadLinksAffectedCount,
    not_audited: notAuditedCount,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Search className="h-4 w-4" />
            Sprawdzanie opublikowanej treści
          </h2>
          <p className="text-xs text-muted-foreground">
            Klik wiersz = rozwija pełen detail audytu (LLM summary, halucynacje, linki, SEO meta).
          </p>
        </div>
        <LiveIndicator isLive={ca.isLive} label="Audyt" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        <StatCard label="Opublikowane" value={publishedArticles.length} variant="default" />
        <StatCard label="OK" value={ca.passedCount} variant="success" />
        <StatCard label="Warnings" value={ca.warningCount} variant="warning" />
        <StatCard label="Failed" value={ca.failedCount} variant="error" />
        <StatCard label="Do przejrzenia" value={issuesCount} variant="review" />
      </div>

      {/* Info card — broken outgoing links (per published artykuł ile linków do nieopublikowanych) */}
      {brokenOutgoingStats.affectedArticles > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-2.5 text-xs flex items-start gap-2">
          <span className="text-base leading-none mt-0.5">⚠</span>
          <div className="flex-1">
            <span className="font-semibold text-amber-700 dark:text-amber-300">
              {brokenOutgoingStats.affectedArticles} opublikowanych artykułów
            </span>
            <span className="text-amber-700/80 dark:text-amber-400/80">{" "}linkuje do{" "}</span>
            <span className="font-semibold text-amber-700 dark:text-amber-300">
              {brokenOutgoingStats.totalBrokenLinks} nieopublikowanych nodów
            </span>
            <span className="text-muted-foreground">{" "}(404 dla użytkownika i Google).</span>
            <div className="text-[11px] text-muted-foreground mt-1">
              Najgorszy artykuł ma <strong className="text-amber-700 dark:text-amber-300">{brokenOutgoingStats.topOffenderCount} broken outgoing</strong>.
              Hover badge <strong className="text-amber-700 dark:text-amber-300">⚠</strong> przy wierszu — zobaczysz konkretne brakujące slugi.
              Akcja: dogeneruj brakujące nody w <em>Treści</em> (sort 🔗 Priorytet linków) albo cofnij publikację w <em>Publikacjach</em>.
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap border rounded-lg p-3 bg-muted/20">
        <Button
          size="sm"
          onClick={handleBulkAudit}
          disabled={ca.loading || publishedArticles.length === 0}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          <Play className="mr-1.5 h-3.5 w-3.5" />
          {ca.bulkProgress
            ? `Audyt ${ca.bulkProgress.current}/${ca.bulkProgress.total}`
            : selected.size > 0
              ? `Audytuj wybrane (${selected.size})`
              : `Audytuj nie-audytowane (${notAuditedCount})`}
        </Button>
        {selected.size === 0 && filteredArticles.length > 0 && (
          <Button size="sm" variant="ghost" onClick={toggleSelectAll} className="text-xs">
            Zaznacz wszystkie
          </Button>
        )}
        <label className="flex items-center gap-2 text-xs cursor-pointer ml-3">
          <Checkbox checked={includeLLM} onCheckedChange={(v) => setIncludeLLM(v === true)} />
          LLM audit (~$0.003/art)
        </label>
        {totalCostUsd > 0 && (
          <span className="text-xs text-muted-foreground ml-2 font-mono">
            ∑ koszt: <strong className="text-foreground">${totalCostUsd.toFixed(6)}</strong>
          </span>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs flex-wrap">
          <span className="text-muted-foreground">Filtr:</span>
          {(Object.keys(FILTER_LABELS) as FilterMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setFilter(m)}
              className={`px-2 py-1 rounded text-[11px] transition ${
                filter === m
                  ? "bg-teal-500/15 text-teal-600 dark:text-teal-400"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {FILTER_LABELS[m]}
              {filterCounts[m] > 0 && (
                <span className="ml-1 opacity-70">({filterCounts[m]})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {publishedArticles.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-sm text-muted-foreground">
          Brak opublikowanych artykułów. Po publish artykuły pojawią się tutaj.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28px]">
                  <Checkbox
                    checked={filteredArticles.length > 0 && selected.size === filteredArticles.length}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-[28px]"></TableHead>
                <TableHead className="min-w-[280px]">Artykuł</TableHead>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead className="w-[60px]">Score</TableHead>
                <TableHead className="w-[100px]">Problemy</TableHead>
                <TableHead className="min-w-[300px]">Podsumowanie LLM</TableHead>
                <TableHead className="w-[150px]">Linki</TableHead>
                <TableHead className="w-[110px]">SEO meta</TableHead>
                <TableHead className="w-[160px]">Schema</TableHead>
                <TableHead className="w-[80px]">Koszt</TableHead>
                <TableHead className="w-[110px]">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredArticles.map((c) => {
                const audit = ca.audits.get(c.node_id);
                const title = nodeTitleMap.get(c.node_id) || c.node_id.slice(0, 12);
                const wpUrl = (c as { wp_post_url?: string }).wp_post_url ?? null;
                const seoTitle = (c as { seo_title?: string }).seo_title || null;
                const metaDesc = (c as { meta_description?: string }).meta_description || null;
                const isExpanded = expanded.has(c.node_id);
                const issueCount = issueCountMap.get(c.node_id) || 0;

                return (
                  <React.Fragment key={c.node_id}>
                    <TableRow
                      className={audit ? "cursor-pointer hover:bg-muted/30" : ""}
                      onClick={audit ? () => toggleExpand(c.node_id) : undefined}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(c.node_id)}
                          onCheckedChange={() => toggleSelect(c.node_id)}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {audit &&
                          (isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span className="line-clamp-1 font-medium" title={title}>
                            {title}
                          </span>
                          {(() => {
                            const outgoing = brokenOutgoingMap.get(c.node_id);
                            if (!outgoing || outgoing.count <= 0) return null;
                            const shown = outgoing.missingSlugs.slice(0, 2);
                            const rest = outgoing.missingSlugs.length - shown.length;
                            return (
                              <span className="flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-400">
                                <span className="font-bold tabular-nums">⚠ {outgoing.count} broken:</span>
                                <span className="text-amber-600/80 dark:text-amber-400/70 truncate">
                                  {shown.map((s) => `/${s}/`).join(", ")}
                                  {rest > 0 ? ` +${rest} więcej` : ""}
                                </span>
                              </span>
                            );
                          })()}
                          {(() => {
                            // STOP-3 link-audit: dead links badge + button "Napraw"
                            const scan = deadLinkScansMap.get(c.node_id);
                            if (!scan || scan.counts.total === 0) return null;
                            return (
                              <span className="flex items-center gap-1.5 text-[10px] flex-wrap">
                                <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400 font-semibold">
                                  <Link2 className="h-2.5 w-2.5" />
                                  {scan.counts.total} martwych:
                                </span>
                                {scan.counts.slug_mismatch > 0 && (
                                  <span className="text-amber-600 dark:text-amber-400">{scan.counts.slug_mismatch} rozjazd</span>
                                )}
                                {scan.counts.pending_target > 0 && (
                                  <span className="text-blue-600 dark:text-blue-400">{scan.counts.pending_target} pending</span>
                                )}
                                {scan.counts.orphan > 0 && (
                                  <span className="text-rose-600 dark:text-rose-400">{scan.counts.orphan} sierota</span>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLinkFixModal({ nodeId: c.node_id });
                                  }}
                                  className="ml-1 px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 text-[10px]"
                                  title="Otwórz podgląd naprawy linków"
                                >
                                  Napraw linki
                                </button>
                              </span>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        {audit ? (
                          <StatusIcon status={audit.audit_status} />
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {audit ? (
                          <span
                            className={
                              audit.overall_score >= 90
                                ? "text-emerald-500"
                                : audit.overall_score >= 70
                                  ? "text-amber-500"
                                  : "text-red-500"
                            }
                          >
                            {audit.overall_score}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {audit && issueCount > 0 ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/5"
                          >
                            <AlertCircle className="mr-0.5 h-2.5 w-2.5" />
                            {issueCount}
                          </Badge>
                        ) : audit ? (
                          <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="mr-0.5 h-2.5 w-2.5" />
                            OK
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-[11px] text-muted-foreground leading-snug">
                        {audit?.layer_llm?.summary ? (
                          <span className="line-clamp-2" title={audit.layer_llm.summary}>
                            {audit.layer_llm.summary}
                          </span>
                        ) : audit ? (
                          <span className="italic">LLM nie wywołany</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">
                        {audit ? (
                          <div className="flex flex-col gap-0.5">
                            <span>
                              <span className="font-mono text-emerald-500">{audit.layer_internal_links?.checked ?? 0}</span> wewnętrznych
                            </span>
                            <span>
                              <span className="font-mono text-blue-500">{audit.layer_external_links?.checked ?? 0}</span> zewnętrznych
                            </span>
                          </div>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-[10px]">
                        {seoTitle && metaDesc ? (
                          <div className="flex flex-col gap-0.5 text-muted-foreground">
                            <span>
                              tytuł: <span className="font-mono text-emerald-500">{seoTitle.length}</span> zn.
                            </span>
                            <span>
                              opis: <span className="font-mono text-emerald-500">{metaDesc.length}</span> zn.
                            </span>
                          </div>
                        ) : (
                          <span className="text-amber-500 italic">brak w DB</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[10px]">
                        {audit?.layer_schema ? (
                          <div className="flex flex-col gap-0.5">
                            <span className={audit.layer_schema.has_blogposting ? "text-emerald-500" : "text-red-500"}>
                              {audit.layer_schema.has_blogposting ? "✓" : "✗"} BlogPosting
                            </span>
                            <span className={audit.layer_schema.has_webpage ? "text-emerald-500" : "text-amber-500"}>
                              {audit.layer_schema.has_webpage ? "✓" : "✗"} WebPage
                            </span>
                            {audit.layer_schema.banned_tokens_found && audit.layer_schema.banned_tokens_found.length > 0 && (
                              <span className="text-red-500">
                                ✗ zakazane tokeny ({audit.layer_schema.banned_tokens_found.length})
                              </span>
                            )}
                          </div>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-[10px] font-mono">
                        {audit && audit.cost_usd > 0 ? (
                          <span className="text-emerald-500">${audit.cost_usd.toFixed(6)}</span>
                        ) : audit ? (
                          <span className="text-muted-foreground/50">free</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {!audit && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleSingleAudit(c.node_id)}
                              disabled={ca.loading}
                            >
                              <Play className="mr-1 h-3 w-3" />
                              Sprawdź
                            </Button>
                          )}
                          {audit && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              title="Podgląd treści (live z WP)"
                              onClick={() => setPreviewDialog({ nodeId: c.node_id, title, wpPostUrl: wpUrl })}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          )}
                          {wpUrl && (
                            <a
                              href={wpUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                              title="Otwórz w WP"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {isExpanded && audit && (
                      <TableRow className="bg-muted/10 border-0">
                        <TableCell colSpan={12} className="py-4 px-6">
                          <ExpandedDetail
                            audit={audit}
                            seoTitleDB={seoTitle}
                            metaDescDB={metaDesc}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <ContentPreviewDialog
        open={!!previewDialog}
        onOpenChange={(o) => { if (!o) setPreviewDialog(null); }}
        nodeId={previewDialog?.nodeId ?? null}
        title={previewDialog?.title ?? ""}
        wpPostUrl={previewDialog?.wpPostUrl ?? null}
      />

      {/* STOP-3 link-audit: modal "Napraw linki" — apply z atomic DB+WP rollback.
          MUSI być w scope PublishedQAPanel (gdzie linkFixModal/setLinkFixModal),
          NIE w sub-komponencie ExpandedDetail. */}
      {linkFixModal && (
        <LinkFixPreviewModal
          open={true}
          onOpenChange={(o) => { if (!o) setLinkFixModal(null); }}
          nodeId={linkFixModal.nodeId}
          mapId={mapId}
          onApplied={() => setScanRefreshTick((x) => x + 1)}
        />
      )}
    </div>
  );
}

// =============================================
// Expanded detail (sub-row)
// =============================================

function ExpandedDetail({
  audit,
  seoTitleDB,
  metaDescDB,
}: {
  audit: ContentAuditResult;
  seoTitleDB: string | null;
  metaDescDB: string | null;
}) {
  const llm = audit.layer_llm;
  const internalLinks = audit.layer_internal_links?.links ?? [];
  const externalLinks = audit.layer_external_links?.links ?? [];
  const completeness = audit.layer_completeness;
  const schema = audit.layer_schema;

  return (
    <div className="space-y-4">
      {/* LLM SUMMARY (centralne) */}
      {llm ? (
        <section className="rounded-lg bg-gradient-to-br from-teal-500/5 to-purple-500/5 border border-teal-500/20 p-3 space-y-2">
          <header className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
              <h4 className="text-xs font-semibold">Audyt semantyczny LLM</h4>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-mono">
              <span>Topic: {llm.semantic_check?.stays_on_topic ? "✓" : "✗"}</span>
              <span>Voice: {llm.semantic_check?.voice_natural ? "✓ naturalny" : "✗"}</span>
              <span>Density: <strong className="text-foreground">{llm.semantic_check?.claim_density}</strong></span>
              <span>Hooks: {llm.content_check?.hooks_natural ? "✓" : "✗"}</span>
              <span>Closure: {llm.content_check?.closure_natural ? "✓" : "✗"}</span>
            </div>
          </header>

          <p className="text-xs leading-relaxed">{llm.summary}</p>

          {llm.semantic_check?.halucinations && llm.semantic_check.halucinations.length > 0 && (
            <div className="space-y-1">
              <h5 className="text-[11px] font-semibold text-red-600 dark:text-red-400">
                Halucynacje ({llm.semantic_check.halucinations.length}):
              </h5>
              <ul className="space-y-0.5 text-[11px]">
                {llm.semantic_check.halucinations.map((h, i) => (
                  <li key={i} className="flex gap-1.5">
                    <Badge
                      variant="outline"
                      className={`shrink-0 text-[9px] ${
                        h.severity === "high"
                          ? "border-red-500 text-red-500"
                          : h.severity === "medium"
                            ? "border-amber-500 text-amber-500"
                            : "border-muted-foreground"
                      }`}
                    >
                      {h.severity}
                    </Badge>
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">[{h.type}]</span>
                    <span className="text-muted-foreground">{h.claim}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {llm.semantic_check?.voice_issues && llm.semantic_check.voice_issues.length > 0 && (
            <div className="flex flex-wrap gap-1 items-center text-[11px]">
              <span className="font-semibold">Voice issues:</span>
              {llm.semantic_check.voice_issues.map((v, i) => (
                <Badge key={i} variant="outline" className="text-[9px]">{v}</Badge>
              ))}
            </div>
          )}

          {llm.fixes_suggested && llm.fixes_suggested.length > 0 && (
            <div className="space-y-1">
              <h5 className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">Sugerowane poprawki:</h5>
              <ol className="space-y-0.5 text-[11px] text-muted-foreground list-decimal list-inside">
                {llm.fixes_suggested.map((fix, i) => (
                  <li key={i} className="leading-relaxed">{fix}</li>
                ))}
              </ol>
            </div>
          )}
        </section>
      ) : (
        <p className="text-xs text-muted-foreground italic">LLM nie został wywołany dla tego audytu.</p>
      )}

      {/* 2-col: Linki + SEO */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <section className="border rounded-lg p-3 space-y-1.5">
          <h5 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
            Linki ({internalLinks.length} wew + {externalLinks.length} zew)
          </h5>
          {internalLinks.length === 0 && externalLinks.length === 0 ? (
            <p className="text-[11px] text-muted-foreground italic">Brak linków w artykule</p>
          ) : (
            <div className="space-y-1.5 text-[11px]">
              {internalLinks.length > 0 && (
                <div>
                  <p className="font-medium text-emerald-600 dark:text-emerald-400 text-[10px]">Wewnętrzne:</p>
                  <ul className="space-y-0.5">
                    {internalLinks.slice(0, 10).map((l, i) => (
                      <LinkRow key={i} url={l.url} anchor={l.anchor} />
                    ))}
                    {internalLinks.length > 10 && (
                      <li className="text-muted-foreground italic text-[10px]">+{internalLinks.length - 10} więcej</li>
                    )}
                  </ul>
                </div>
              )}
              {externalLinks.length > 0 && (
                <div>
                  <p className="font-medium text-blue-600 dark:text-blue-400 text-[10px]">Zewnętrzne:</p>
                  <ul className="space-y-0.5">
                    {externalLinks.slice(0, 10).map((l, i) => (
                      <LinkRow key={i} url={l.url} anchor={l.anchor} />
                    ))}
                    {externalLinks.length > 10 && (
                      <li className="text-muted-foreground italic text-[10px]">+{externalLinks.length - 10} więcej</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="border rounded-lg p-3 space-y-2">
          <h5 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">SEO meta (DB)</h5>
          <div className="space-y-1.5 text-[11px]">
            <div className="space-y-0.5">
              <p className="text-muted-foreground flex items-center gap-2 text-[10px]">
                <span>seo_title</span>
                {seoTitleDB && (
                  <Badge
                    variant="outline"
                    className={`text-[9px] ${
                      seoTitleDB.length >= 30 && seoTitleDB.length <= 70
                        ? "border-emerald-500 text-emerald-600"
                        : "border-amber-500 text-amber-600"
                    }`}
                  >
                    {seoTitleDB.length} ch
                  </Badge>
                )}
              </p>
              <p className="font-mono">
                {seoTitleDB || <span className="text-muted-foreground italic">(brak)</span>}
              </p>
            </div>
            <div className="space-y-0.5">
              <p className="text-muted-foreground flex items-center gap-2 text-[10px]">
                <span>meta_description</span>
                {metaDescDB && (
                  <Badge
                    variant="outline"
                    className={`text-[9px] ${
                      metaDescDB.length >= 120 && metaDescDB.length <= 170
                        ? "border-emerald-500 text-emerald-600"
                        : "border-amber-500 text-amber-600"
                    }`}
                  >
                    {metaDescDB.length} ch
                  </Badge>
                )}
              </p>
              <p className="font-mono">
                {metaDescDB || <span className="text-muted-foreground italic">(brak)</span>}
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Structure bar */}
      <div className="flex items-center gap-4 flex-wrap text-[10px] text-muted-foreground border-t pt-2">
        {completeness && (
          <span>
            <strong className="text-foreground font-mono">{completeness.word_count}</strong> słów ·
            H2: <strong className="text-foreground">{completeness.h2_count}</strong> ·
            H3: <strong className="text-foreground">{completeness.h3_count}</strong>
            {completeness.h1_count > 0 && (
              <span className="text-amber-500"> · H1 w content: {completeness.h1_count} (duplikat?)</span>
            )}
          </span>
        )}
        {schema && (
          <span>
            Schema:
            {schema.has_blogposting ? <span className="text-emerald-500"> BlogPosting ✓</span> : <span className="text-red-500"> BlogPosting ✗</span>}
            {schema.has_webpage ? <span className="text-emerald-500"> · WebPage ✓</span> : <span className="text-amber-500"> · WebPage ✗</span>}
            {schema.banned_tokens_found && schema.banned_tokens_found.length > 0 && (
              <span className="text-red-500"> · banned: {schema.banned_tokens_found.join(", ")}</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function LinkRow({ url, anchor }: { url: string; anchor: string }) {
  return (
    <li className="flex items-start gap-1 leading-snug">
      <span className="text-muted-foreground/60 shrink-0">•</span>
      <div className="flex-1 min-w-0">
        <span className="text-foreground font-medium">{anchor || <em className="text-muted-foreground/60">[pusty]</em>}</span>
        <span className="text-muted-foreground"> → </span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline break-all">
          {url.replace(/^https?:\/\//, "")}
        </a>
      </div>
    </li>
  );
}

function StatCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "default" | "success" | "warning" | "error" | "review";
}) {
  const colors = {
    default: "bg-muted/30 text-foreground",
    success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    error: "bg-red-500/10 text-red-700 dark:text-red-400",
    review: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
  };
  return (
    <div className={`rounded-lg px-3 py-2 ${colors[variant]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-semibold font-mono">{value}</div>
    </div>
  );
}
